import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart, PatientDoc, IsSigningObject, FileTypes } from '@usacsmideo/types';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { FormInput } from '../../../../components/forms/FormInput';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { useInitChartSection } from '../../../../hooks/useInitChartSection';
import { notifyChartSaveError } from '../../../../utilities/chartSaveError';
import { SignatureArea } from '../../../../components/SignatureArea';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { Heading } from '../../../../components/Heading';
import { ChartInitError } from '../../../../components/ChartInitError';
import * as Sentry from '@sentry/browser';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
export const Route = createFileRoute('/_authenticated/$patientId/payment/aob')({
  component: AOBPage,
});

function AOBPage() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientInfoQuery = usePatientDoc(patientId);
  const patientInfo = patientInfoQuery.data as PatientDoc;
  const { mutate, mutateAsync: mutateChartAsync } = useUpdateChartSection();

  const [isSigning, setIsSigning] = React.useState({
    patientFinal: false,
    witness1: false,
    witness2: false,
    hipaa: false,
    consent: false,
    forms: false,
    abnMedicare: false,
    abn: false,
    aob1: false,
    aob2: false,
  } as IsSigningObject);
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    try {
      // Mark payment as complete for insurance/medicare patients (AOB is their final payment step)
      await mutateChartAsync({
        patientId: patientId,
        docData: {
          PAYMENT_INFO: {
            alreadyPaid: true,
            amountPaid: 0,
          },
        },
      });

      // Update nav object and navigate to next screen
      mutatePatientDoc(
        {
          patientId: patientId,
          navObj: res.updatedNavObj,
        },
        {
          onSuccess: () => {
            // NAVIGATE TO THE NEXT SCREEN
            navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
    } catch (error) {
      Sentry.captureException(error);
      alert(`Error updating payment info: ${(error as Error).message}`);
      console.error(error);
    }
  };

  // THE FORM DATA TO WRITE — SHARED BY THE SUBMIT AND THE BACKGROUND INIT, SO BOTH WRITE THE SAME
  const aobPayload = () => ({
    patientId: patientId,
    docData: {
      ASSIGNMENT_OF_BENEFITS: {
        reason: aobForm.state.values.reason,
        signature1DataURI: chart?.ASSIGNMENT_OF_BENEFITS?.signature1DataURI
          ? chart.ASSIGNMENT_OF_BENEFITS.signature1DataURI
          : '',
        signature2DataURI: chart?.ASSIGNMENT_OF_BENEFITS?.signature2DataURI
          ? chart.ASSIGNMENT_OF_BENEFITS.signature2DataURI
          : '',
      },
    },
  });

  // WRITE FORM DATA TO FIRESTORE
  const handleAOBUpdate = () => {
    mutate(aobPayload(), {
      onSuccess: () => {
        // alert('Successfully updated document');
      },
      onError: () => {
        notifyChartSaveError();
      },
    });
  };

  // INITIALIZE EMPTY FIRESTORE DOC IF IT DOESN'T EXIST — ONCE PER FAILURE, NOT ONCE PER SNAPSHOT
  const aobInit = useInitChartSection({
    needed: !!chart && !chart.ASSIGNMENT_OF_BENEFITS,
    write: () => mutateChartAsync(aobPayload()),
  });

  // INITIALIZE FORM
  const aobForm = useForm({
    defaultValues: {
      reason: chart?.ASSIGNMENT_OF_BENEFITS?.reason ? chart.ASSIGNMENT_OF_BENEFITS.reason : '',
      signature1DataURI: chart?.ASSIGNMENT_OF_BENEFITS?.signature1DataURI
        ? chart.ASSIGNMENT_OF_BENEFITS.signature1DataURI
        : '',
      signature2DataURI: chart?.ASSIGNMENT_OF_BENEFITS?.signature2DataURI
        ? chart.ASSIGNMENT_OF_BENEFITS.signature2DataURI
        : '',
    },
    onSubmit: async () => {
      await handleAOBUpdate();
    },
  });

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'aob', patientInfo, value: true });
    }
  }, [patientInfo]);

  if (chartQuery.isLoading || patientInfoQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Attempting to load current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.ASSIGNMENT_OF_BENEFITS) {
    return (
      <>
        <div id="medicare-abn-page" className="flex flex-col items-center justify-center">
          <Heading id="medicare-abn-page-header" textContent="Assignment of Benefits (AOB)" />
          <div id="medicare-abn-page-main" className="flex w-5/6 flex-row justify-around">
            <div className="flex flex-col items-start">
              <div className="my-4">
                <p className="text-2xl font-bold">
                  Statement to Permit Payment of Insurance/Medicare Benefits to Provider, Physicians and Patient.
                </p>
                <p id="statement" className="text-xl">
                  I request payment of authorized Insurance/Medicare benefits to me or on my behalf, for any service(s)
                  furnished to me by MIDEO Health, including physician services. I authorize any holder of medical and
                  other information about me to be released to my insurance company/Medicare and its agents for any
                  information needed to determine these benefits or benefits for related services. I understand I am
                  responsible and will pay MIDEO Health for any health insurance deductibles, co-insurance(s)/co-pays,
                  or services not covered by my insurance/Medicare program as defined by the Social Security Act.
                </p>
              </div>
              <div className="my-4">
                <p className="text-2xl font-bold">Assignment of Insurance/Medicare Benefits</p>
                <p id="assignment" className="text-xl">
                  I hereby assign to, and authorize that payment be made directly to MIDEO Health for all benefits
                  otherwise payable to me under the terms of my insurance policies (including Major Medical policies),
                  by reason of the service(s) described in statements rendered by MIDEO Health. MIDEO Health shall
                  refund any payments in excess of its full regular charge(s) to the person(s) entitled to receive the
                  same benefits. I understand that I am financially responsible for all charges not covered, or unpaid
                  by my insurance within a reasonable time.
                </p>
              </div>
              <div className="my-4">
                <p className="text-xl font-bold">SIGNATURE OF BENEFICIARY:</p>
                <SignatureArea
                  elementId={'aob-beneficiary-sig'}
                  type={FileTypes.aobSig1}
                  data={chart}
                  patientId={patientId}
                  isSigning={isSigning}
                  cloudStoragePath={`/user/${patientId}/signatures/ASSIGNMENT_OF_BENEFITS_Beneficiary_Signature`}
                  setIsSigning={setIsSigning}
                  docToUpdate={'ASSIGNMENT_OF_BENEFITS'}
                  sigName="aob1"
                  canComeFromClone={true}
                />
              </div>
              <div className="my-4">
                <p className="text-xl font-bold">SECONDARY SIGNATURE:</p>
                <SignatureArea
                  elementId={'aob-other-sig'}
                  type={FileTypes.aobSig2}
                  data={chart}
                  patientId={patientId}
                  isSigning={isSigning}
                  cloudStoragePath={`/user/${patientId}/signatures/ASSIGNMENT_OF_BENEFITS_Other_Signature`}
                  setIsSigning={setIsSigning}
                  docToUpdate={'ASSIGNMENT_OF_BENEFITS'}
                  sigName="aob2"
                  canComeFromClone={false}
                />
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  aobForm.handleSubmit();
                }}
              >
                <div className="my-4 flex flex-row">
                  <aobForm.Field
                    name="reason"
                    children={(field) => (
                      <>
                        <FormInput
                          label="Reason for Second Signature: "
                          id="reason-for-second-signature"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
              <Button
                id="submit-aob-info"
                type="submit"
                textContent="Submit"
                onClick={handleAOBUpdate}
                disabled={!chart.ASSIGNMENT_OF_BENEFITS.signature1DataURI}
              />
            </div>
          </div>
          <div id="nav" className="flex flex-row">
            <div className="flex flex-col items-center">
              <button
                id="submit-form-info"
                title="Submit Form Info"
                onClick={handleNextButtonClick}
                disabled={false}
                className={classNames.nextButton}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // THE PAGE RENDERS ONLY ONCE ITS SECTION EXISTS, SO A REJECTED INIT WOULD OTHERWISE LEAVE IT ON
  // "Loading..." FOREVER. OFFER THE RETRY INSTEAD (CU-17tm466jq2e).
  if (aobInit.failed) {
    return <ChartInitError onRetry={aobInit.rearm} />;
  }

  return <div>Loading...</div>;
}
